"use client";
import React, { useEffect, useRef, useState } from "react";
import Footer from "../Footer/footer";
import Link from "next/link";
import Image from "next/image";
import ScrollButton from "../Common/scrollButton";
import LogoHeader from "../Common/logoHeader";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import QuoteTabs from "../QuoteTool/quoteTabs";
import parse from "html-react-parser";
import FAQItem from "../FAQ/faqItem";
import QuoteToolHeader from "../QuoteTool/quoteHeader";

interface WeddingPageProps {
  city_name: any;
  city_content: any;
  quote_tool_section: any;
  wedding_review_section: any;
  seasonal_bouquets: any;
}

interface WeddingCityLandingProps {
  weddingPage: WeddingPageProps;
}

export const WeddingCityLanding: React.FC<WeddingCityLandingProps> = ({
  weddingPage
}) => {
  const weddingQuoteSectionRef = useRef<HTMLDivElement | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const testimonialRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [showCaseCurrentIndex, setSHowcaseCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const MAX_BOUQUET_DESKTOP = 3;

  const scrollToQuoteSection = () => {
    weddingQuoteSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const testimonialSection = weddingPage.wedding_review_section;
  const seasonalBouquets = weddingPage.seasonal_bouquets;
  const bouquets = seasonalBouquets.bouquets;
  const quoteSection = weddingPage.quote_tool_section;
  const { calCards, valueProps, faqs } = weddingPage.city_content;
  const city = weddingPage.city_name;

  useEffect(() => {
    const updateHeight = () => {
      const heights = testimonialRefs.current.map(
        (ref) => ref?.offsetHeight || 0
      );
      const maxHeight = Math.max(...heights);
      setContainerHeight(maxHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, [testimonialSection.testimonials]);

  const nextTestimonial = () => {
    if (currentIndex === testimonialSection.testimonials.length - 1) {
      setCurrentIndex(0);
    } else {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }
  };

  const prevTestimonial = () => {
    if (currentIndex === 0) {
      setCurrentIndex(testimonialSection.testimonials.length - 1);
    } else {
      setCurrentIndex(
        (prevIndex) =>
          (prevIndex - 1 + testimonialSection.testimonials.length) %
          testimonialSection.testimonials.length
      );
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const nextBouquet = () => {
    if (isMobile) {
      setSHowcaseCurrentIndex((prevIndex) => (prevIndex + 1) % bouquets.length);
    } else {
      setSHowcaseCurrentIndex(
        (prevIndex) =>
          (prevIndex + 1) % (bouquets.length - MAX_BOUQUET_DESKTOP + 1)
      );
    }
  };

  const prevBouquet = () => {
    if (isMobile) {
      setSHowcaseCurrentIndex(
        (prevIndex) => (prevIndex - 1 + bouquets.length) % bouquets.length
      );
    } else {
      setSHowcaseCurrentIndex(
        (prevIndex) =>
          (prevIndex - 1 + (bouquets.length - MAX_BOUQUET_DESKTOP + 1)) %
          (bouquets.length - MAX_BOUQUET_DESKTOP + 1)
      );
    }
  };

  const getVisibleBouquets = () => {
    if (isMobile) {
      return [bouquets[showCaseCurrentIndex]];
    } else {
      return [
        bouquets[showCaseCurrentIndex],
        bouquets[(showCaseCurrentIndex + 1) % bouquets.length],
        bouquets[(showCaseCurrentIndex + 2) % bouquets.length]
      ];
    }
  };

  const recipientWindow = {
    delivery_sub_header: quoteSection.delivery_sub_header,
    delivery_description: quoteSection.delivery_description,
    delivery_toggle_btn_text: quoteSection.delivery_toggle_btn_text,
    delivery_toggle_header: quoteSection.delivery_toggle_header,
    delivery_toggle_body: quoteSection.delivery_toggle_body
  };

  const deliveryWindow = {
    delivery_window_header: quoteSection.delivery_window_header,
    delivery_window_body: quoteSection.delivery_window_body,
    delivery_window_sub_description:
      quoteSection.delivery_window_sub_description
  };

  const summaryWindow = {
    summary_product_header: quoteSection.summary_product_header,
    summary_recipient_header: quoteSection.summary_recipient_header,
    summary_recipient_description: quoteSection.summary_recipient_description
  };

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div>
      <div className=" relative min-h-[600px] w-full">
        <div className="desktop-padding relative z-10 desktop-padding">
          <LogoHeader />
          <div className="p-5 md:pt-12 md:pb-24">
            <main className="grid grid-cols-1 md:grid-cols-2 gap-6  md:gap-12">
              <div className="px-[3px] gap-4">
                <h4 className="h4">{`Easy Ordering, In-Season, Budget-Friendly Wedding Flowers ${city}`}</h4>
                <h4 className="h1 text-primary my-4">
                  {`Dreamy Wedding Flowers in ${city}`}
                </h4>
                <p className="body-reg hidden md:block">{`We choose super-fresh flowers and greens to make gorgeous, seasonal wedding bouquets. Our ${city} floral designers handcraft bridal bouquets,
                 corsages, boutonnieres, along with many other wedding floral essentials and decor, delivered directly to you from our hands to yours.`}</p>
                <div className="pt-4 gap-[10px]">
                  <ScrollButton
                    variant="outline"
                    size="fixed"
                    className="clear-btn-primary !font-bold"
                    name="Get Quote"
                    onClick={scrollToQuoteSection}
                  ></ScrollButton>
                </div>
              </div>
              <div
                className="relative py-8 w-full bg-[url('/blue_blob_1.png')] bg-no-repeat bg-cover bg-center mb-8 md:hidden
          "
              >
                <Image
                  src={`https://cdn.buttercms.com/rtVpkZkQmK2QtNVzvPtc`}
                  alt={`wedding hero banner`}
                  className="h-auto w-full border border-[#1717171A]"
                  width={1000}
                  height={500}
                />
                <div className="absolute -bottom-2  right-1/2 transform translate-x-1/2 z-10 flex h-[73px] w-[261px]">
                  <div className="bg-primary font-filson-pro font-[800] text-2xl leading-5 text-white p-3 flex justify-center items-center">
                    100%
                  </div>
                  <h4 className="h4 bg-white py-2 px-3 flex justify-center text-center text-black">
                    {`Florals always will match the photos online`}
                  </h4>
                </div>
              </div>

              <div className="hidden md:block relative">
                <div className="relative w-full h-full">
                  <Image
                    src={`https://cdn.buttercms.com/rtVpkZkQmK2QtNVzvPtc`}
                    alt={`wedding hero banner desktop`}
                    layout="fill"
                    objectFit="cover"
                    className="border fill-inherit border-[#1717171A]"
                    quality={100}
                  />
                  <div className="absolute top-0 left-0 -translate-x-[32px] -translate-y-[32px] w-[220px] h-[235px]">
                    <div className="relative h-full">
                      <Image
                        src={`https://cdn.buttercms.com/Hnzg8JzgR6ifWwICeVnK`}
                        alt={`Wedding banner image 2 (please add a descriptive alt text)`}
                        fill
                        style={{ objectFit: "cover" }}
                        className="hidden sm:block shadow-[0px_4px_10px_0px_#0000001A] border border-[#1717171A]"
                      />
                      <div className="absolute shadow-[0px_4px_10px_0px_#0000001A] h-20 top-0 right-0 bg-primary font-filson-pro font-[800] text-2xl leading-5 text-white p-3 flex justify-center items-center text-center">
                        100%
                      </div>
                      <h4 className="absolute shadow-[0px_4px_10px_0px_#0000001A] top-0 left-full w-40  h-20 h4 bg-white py-2 px-3 flex justify-center items-center text-center text-black">
                        {`Florals always will match the photos online`}
                      </h4>
                    </div>
                  </div>
                  <div className="absolute bottom-0 right-0 translate-x-[32px] translate-y-[32px] w-[220px] h-[235px]">
                    <div className="relative h-full">
                      <Image
                        src={`https://cdn.buttercms.com/YwMspRSFmScykeJgHbdQ`}
                        alt={`Wedding banner image 3 (please add a descriptive alt text)`}
                        fill
                        style={{ objectFit: "cover" }}
                        className="hidden sm:block shadow-[0px_4px_10px_0px_#0000001A] border border-[#1717171A]"
                      />
                      <h4 className="absolute top-0 left-0 -translate-x-[40%] -translate-y-[40%] hidden sm:block w-[109px] h-auto z-10 bg-primary text-white p-3 text-center h4 shadow-[0px_4px_10px_0px_#0000001A]">
                        {`Make Perfect Guarantee`}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
      <div ref={weddingQuoteSectionRef}>
        <div
          className="bg-secondary flex w-full items-center justify-center py-5 sm:py-16 sm:px-8 lg:px-0"
          id="quote"
        >
          <div className="w-full">
            <div className="desktop-padding p-0 ">
              <QuoteToolHeader h4={`Local delivery in ${city} & surrounding areas`} h2={`Stress-free wedding flower arrangements at unbeatable value`} />
            </div>
            <div className="border-t-2 border-b-2 border-gray-light mt-8 ">
              <div
                className=" pb-4 pt-6 md:py-4 sm:p-4 desktop-padding"
                ref={containerRef}
              >
                <QuoteTabs
                  steps={quoteSection.screen}
                  estimationTitle={quoteSection.estimation_title}
                  backText={quoteSection.back_btn_text}
                  bqtypes={quoteSection.bq_types}
                  bulkTypes={quoteSection.bulk_types}
                  recipientWindow={recipientWindow}
                  deliveryWindow={deliveryWindow}
                  summaryWindow={summaryWindow}
                  containerRef={containerRef}
                  startTab={"date"}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-light px-4 sm:px-8 2xl:px-0 py-5 sm:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="lg:grid lg:grid-cols-2 gap-5 sm:gap-6 lg:gap-12">
            <div className="pt-9 pb-6 py-[3px]">
              <div className="space-y-4">
                <h4 className="h4">{`Most-Loved ${city} Wedding Flowers`}</h4>
                <h2 className="mt-2 h2 sm:h1 text-primary">
                  {`From our community`}
                </h2>
                <p className="body-reg">{`Loved by 300,000+ Canadians, happily married couples & guests!`}</p>
                <div className="flex gap-6 pt-4">
                  <Button
                    className="border border-primary bg-transparent text-primary rounded-none  hover:!text-white "
                    onClick={prevTestimonial}
                  >
                    <ChevronLeft size={48} className="h-12 w-12" />
                  </Button>
                  <Button
                    className="border border-primary bg-transparent text-primary rounded-none  hover:!text-white "
                    onClick={nextTestimonial}
                  >
                    <ChevronRight size={48} className="h-12 w-12" />
                  </Button>
                </div>
              </div>
              <div className="pt-8 pr-12 hidden sm:block">
                <p className="text-primary body-lg-bold pb-2">
                  {testimonialSection.top_topic}
                </p>
                <div className="flex justify-between h-[45px]">
                  {testimonialSection.logos.map((logo: any, index: any) => (
                    <Image
                      key={index}
                      src={logo.image}
                      alt={logo.image_alt}
                      width={163}
                      height={45}
                      className="opacity-25 "
                    />
                  ))}
                </div>
              </div>
              <div className="body-lg-bold text-primary text-right pt-14 px-12 hidden sm:block">
                {testimonialSection.bottom_topic}
              </div>
            </div>
            <div className="py-[10px] overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {testimonialSection.testimonials.map((testimonial: any, index: any) => (
                  <div
                    key={index}
                    className="w-full flex-shrink-0"
                    ref={(el) => {
                      testimonialRefs.current[index] = el;
                    }}
                  >
                    <div className="relative w-full h-[250px] sm:h-[337px]">
                      <Image
                        src={testimonial.image}
                        alt={testimonial.image_alt}
                        fill
                        style={{ objectFit: "cover" }}
                      />
                    </div>
                    <div className="mt-4 space-y-4">
                      <Image
                        src="https://cdn.buttercms.com/3w9j63mTTEO5PWna8gTq"
                        width={48}
                        height={48}
                        alt="quote image"
                      />
                      <blockquote>
                        <div className="body-lg">{testimonial.quote}</div>
                      </blockquote>
                      <div className="mt-4">
                        <div className="body-reg-bold">
                          {testimonial.author}
                        </div>
                        <div className="body-reg underline">
                          <Link href={testimonial.link} target="_blank">
                            {testimonial.author_handle}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <section className="bg-secondary sm:px-4 py-4 2xl:px-0 md:pt-16 md:pb-4">
        <div className="max-w-7xl mx-auto gap-6 space-y-6">
          <div className="space-y-4 ">
            <h4 className="text-center h4">{`Discover what's in season for weddings & brides`}</h4>
            <h2 className="h2 sm:h1 text-center text-primary mb-4">
              {`Our Bridal Bouquets`}
            </h2>
            <p className="text-center body-reg mb-8">
              {`We choose super-fresh flowers and greens to make gorgeous, seasonal bouquets perfect for ${city} weddings.`}
            </p>
          </div>
          <div className="bg-teal-50 px-4 pb-4  overflow-hidden">
            <div className="relative h-[420px] p-4 flex justify-center items-center space-x-4 transition-transform duration-500 ease-in-out">
              <div
                className="py-4 absolute top-0 left-0 w-full h-full flex transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(-${showCaseCurrentIndex * (isMobile ? 100 : 33.4)
                    }%)`
                }}
              >
                {bouquets.map((bouquet: any, index: any) => (
                  <div
                    key={index}
                    className="w-full sm:w-1/2 md:w-1/3 flex-shrink-0 p-4"
                  >
                    <Image
                      src={bouquet.image}
                      alt={bouquet.image_alt}
                      width={420}
                      height={420}
                      className="w-full h-full object-cover "
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between items-center">
              <Button
                className="border border-primary bg-transparent text-primary rounded-none  hover:!text-white "
                onClick={prevBouquet}
              >
                <ChevronLeft size={8} className="h-2 w-2" />
              </Button>
              <ScrollButton
                variant="outline"
                size=""
                className="clear-btn-primary"
                name=" Get Quote"
                onClick={scrollToQuoteSection}
              ></ScrollButton>
              <Button
                className="border border-primary bg-transparent text-primary rounded-none  hover:!text-white "
                onClick={nextBouquet}
              >
                <ChevronRight size={8} className="h-2 w-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>
      <section className="pt-4 pb-8 bg-white px-4 sm:px-8 2xl:px-0">
        <div className="container max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {valueProps.map((prop: any, index: any) => (
              <div
                key={index}
                className="flex flex-col items-center text-center gap-1 md:gap-4"
              >
                <Image
                  src={prop.image}
                  width={64}
                  height={64}
                  alt={prop.image_alt}
                />
                <div className="body-reg-bold">{prop.heading}</div>
                <div className="!boy-reg">{parse(prop.content)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div>
        <div className="max-w-7xl mx-auto text-center pt-9 pb-6 px-4 2xl:px-0 space-y-4">
          <h4 className="h4 pt-4 ">{`Loved by 5,000+ weddings & brides`}</h4>
          <h2 className="h2 sm:h1 text-primary">
            {`Most-Loved Fresh Flowers`}
          </h2>
          <p className="body-reg  mx-auto py-2">
            {`Bridal bouquets, bridesmaid bouquets, corsages, bud vases, and more, created fresh by our ${city} designers for your big day`}
          </p>
        </div>
        <section className="sm:px-8 lg:px-0">
          <div className="container mx-auto px-0 max-w-7xl">
            <div className="">
              {calCards.map((card: any, index: any) => (
                <div key={index} className="overflow-hidden">
                  <div className="hidden md:grid md:grid-cols-2 gap-6 md:gap-24">
                    {index % 2 === 0 ? (
                      <>
                        <Image
                          src={card.image}
                          alt={card.image_alt}
                          width={595}
                          height={505}
                          className="w-full h-[400px] sm:h-[505px] object-cover"
                        />
                        <div className="flex flex-col justify-center items-start gap-6">
                          <div className="px-2 sm:px-0 py-2 body-lg-bold">
                            {card.heading}
                          </div>
                          <div
                            className="body-reg px-2 pb-2 sm:pb-0 sm:px-0"
                            dangerouslySetInnerHTML={{ __html: card.content }}
                          ></div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-col justify-center items-start gap-6">
                          <div className="px-2 sm:px-0 py-2 body-lg-bold">
                            {card.heading}
                          </div>
                          <div
                            className="body-reg px-2 pb-2 sm:pb-0 sm:px-0"
                            dangerouslySetInnerHTML={{ __html: card.content }}
                          ></div>
                        </div>
                        <Image
                          src={card.image}
                          alt={card.image_alt}
                          width={595}
                          height={505}
                          className="w-full h-[400px] sm:h-[505px] object-cover"
                        />
                      </>
                    )}
                  </div>
                  <div className="block md:hidden p-4">
                    <>
                      <Image
                        src={card.image}
                        alt={card.image_alt}
                        width={595}
                        height={505}
                        className="w-full h-[400px] sm:h-[505px] object-cover"
                      />
                      <div className="flex flex-col justify-center items-start gap-2 pt-2">
                        <div className="px-2 sm:px-0 py-2 body-lg-bold">
                          {card.heading}
                        </div>
                        <div
                          className="body-reg px-2 "
                          dangerouslySetInnerHTML={{ __html: card.content }}
                        ></div>
                      </div>
                    </>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <div className="flex items-center justify-center pt-8">
        <div className="max-w-7xl w-full bg-primary overflow-hidden grid grid-cols-1 md:grid-cols-2 sm:mx-4 md:mx-0 sm:rounded p-5 md:p-0">
          <div className="hidden sm:block p-12 text-base space-y-4">
            <h4 className="text-white h4">{`Most-Loved Flower Delivery ${city}`}</h4>
            <h2 className="text-white h3 sm:h1 py-1">{`${city}'s Best Online Wedding Flowers`}</h2>
            <p className="text-white body-reg">{`Planning a wedding can be overwhelming in ${city}, especially when it comes to budgeting. Our goal is to help make your wedding flower
             plan easy and stay within your budget while walking down the aisle with the bridal bouquet and flowers of your dreams.`}</p>

            <ScrollButton
              size=""
              variant="outline"
              className=" text-secondary my-2 rounded-none body-reg-bold"
              name="Get Started"
              onClick={scrollToQuoteSection}
            ></ScrollButton>
          </div>

          {/* Right Section */}
          <div className="relative pb-5 md:pb-0 hidden md:block">
            <Image
              src={`https://cdn.buttercms.com/IEhDKPQISUCSpaXXwFzz`}
              alt={``}
              fill
              style={{ objectFit: "cover" }}
              className="object-cover w-full h-full rounded-none"
            />

            <h4 className="absolute w-36  top-4 right-4 bg-white p-3 h4 text-center">
              {`Always fresh & in season`}
            </h4>
            <div className="absolute bottom-4 left-4 bg-white p-2 w-28">
              <div className="flex justify-center items-center">
                <Image
                  src={`https://cdn.buttercms.com/dsGsX1juRn6snFDEr0dv`}
                  alt="100% Callia Guarantee"
                  width={60}
                  height={60}
                />
              </div>
              <h4 className="h4 text-primary text-center">
                {`100% Callia Guarantee`}
              </h4>
            </div>
          </div>
          <div className=" pb-5 md:pb-0 block md:hidden">
            <Image
              src={`https://cdn.buttercms.com/8p7gdiRR7uLT00k2MDcA`}
              alt={``}
              width={319}
              height={342}
              className=" w-full h-full rounded-none"
            />
          </div>
          <div className="block sm:hidden text-base space-y-5 ">
            <h4 className="text-white h4">{`Most-Loved Flower Delivery ${city}`}</h4>
            <h4 className="text-white h2 sm:h1 py-1">{`${city}'s Best Online Wedding Flowers`}</h4>
            <p className="text-white body-reg">{`Planning a wedding can be overwhelming in ${city}, especially when it comes to budgeting. Our goal is to help make your 
            wedding flower plan easy and stay within your budget while walking down the aisle with the bridal bouquet and flowers of your dreams.`}</p>
            <ScrollButton
              size=""
              variant="outline"
              className=" text-secondary my-2 body-reg-bold"
              name="Get Started"
              onClick={scrollToQuoteSection}
            ></ScrollButton>
          </div>
        </div>
      </div>
      <div>
        {faqs.length > 0 && (
          <div className="flex justify-center text-center bg-white pt-6 pb-12 sm:!py-20">
            <div className="w-full sm:w-[60%]">
              <h3 className="h3 text-primary mb-6 sm:mb-10">{`FAQs`}</h3>
              <div className="flex justify-center">
                <div className="flex justify-center">
                  <div className="text-left w-[90%]">
                    <hr />
                    {faqs.map((item: any, index: any) => (
                      <div key={`faq_${index}`}>
                        <FAQItem
                          question={item["question"]}
                          answer={item["answer"]}
                        />
                        <hr />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer locale="en-ca" />
    </div>
  );
};
